import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const MultiSelect = ({ 
  label, 
  name, 
  value = [], 
  onChange, 
  options,
  required = false,
  error,
  className = '',
  placeholder = 'Seçiniz...'
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = (optionValue) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    
    // Create synthetic event
    const event = {
      target: {
        name: name,
        value: newValue
      }
    };
    onChange(event);
  };

  const handleRemove = (optionValue) => {
    const newValue = value.filter(v => v !== optionValue);
    const event = {
      target: {
        name: name,
        value: newValue
      }
    };
    onChange(event);
  };

  const getLabel = (val) => {
    const option = options.find(opt => opt.value === val);
    return option ? option.label : val;
  };

  return (
    <div className={`mb-4 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
          {required && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full min-h-[42px] px-3 py-2 border rounded-md cursor-pointer bg-white dark:bg-gray-800 text-left focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            error ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'
          }`}
        >
          {value.length === 0 ? (
            <span className="text-gray-400 dark:text-gray-500">{placeholder}</span>
          ) : (
            <div className="flex flex-wrap gap-2">
              {value.map((val) => (
                <span
                  key={val}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 rounded text-sm"
                >
                  {getLabel(val)}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(val);
                    }}
                    className="hover:text-primary-600 dark:hover:text-primary-400"
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </button>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            <div className="relative z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-xl max-h-60 overflow-auto">
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleToggle(option.value);
                  }}
                  className={`w-full px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 text-left transition-colors ${
                    value.includes(option.value) ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                  }`}
                >
                  <label className="flex items-center gap-2 cursor-pointer pointer-events-none select-none">
                    <input
                      type="checkbox"
                      checked={value.includes(option.value)}
                      readOnly
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-gray-900 dark:text-gray-100">{option.label}</span>
                  </label>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {error && <p className="mt-1 text-sm text-red-500 dark:text-red-400">{error}</p>}
    </div>
  );
};

export default MultiSelect;
